import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  CardCoverageStatus,
  So5LeaderboardType,
} from '@sorare/core/src/__generated__/globalTypes';
import { Ineligible } from '@sorare/core/src/atoms/icons/Ineligible';
import { Market } from '@sorare/core/src/atoms/icons/Market';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { messages } from '@sorare/core/src/lib/cardCoverage';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import { BasicBenchExtraWrapper } from '@sorare/marketplace/src/components/searchPlayerTable/BasicBenchExtraWrapper';

import { BenchExtraTooltip } from '#src/components/compose/BenchExtraTooltip/index.tsx';

import { BenchExtraInjury } from './BenchExtraInjury/index.tsx';
import { BenchExtraSuspension } from './BenchExtraSuspension/index.tsx';
import { BenchExtraUsedIn } from './BenchExtraUsedIn/index.tsx';
import type {
  BenchExtra_anyCardInterface,
  BenchExtra_anyPlayerInterface,
  BenchExtra_so5Lineup,
  BenchExtra_suspension,
} from './__generated__/index.graphql.ts';

const EmptyIcon = styled.div`
  width: var(--intermediate-unit);
  height: var(--intermediate-unit);
`;

type Props = {
  anyCard: BenchExtra_anyCardInterface | undefined;
  anyPlayer: BenchExtra_anyPlayerInterface;
  suspensions: BenchExtra_suspension[];
  hasMultipleGames?: boolean;
  so5LineupId?: string;
  taskId?: string;
  iconOnly?: boolean;
  price?: MonetaryAmount | null;
  defaultNode?: ReactNode;
  showInjury?: boolean;
  showSuspension?: boolean;
};

export const BenchExtra = ({
  so5LineupId,
  taskId,
  anyCard,
  anyPlayer,
  suspensions,
  hasMultipleGames,
  iconOnly,
  price,
  showInjury = true,
  showSuspension = true,
}: Props) => {
  const { main, exponent } = useAmountWithConversion({
    monetaryAmount: price,
  });
  const usedInLineups =
    anyCard && isType(anyCard.usedIn, 'UsedInSo5Lineups')
      ? anyCard?.usedIn?.so5Lineups
      : undefined;

  const lineupUsingCard = usedInLineups?.find(
    so5Lineup => so5Lineup && so5Lineup.id !== so5LineupId
  );

  const tasksUsingCard =
    anyCard && isType(anyCard.usedIn, 'UsedInTasks')
      ? anyCard?.usedIn?.tasks.filter(task => task.id !== taskId)
      : undefined;

  const listed = !!(anyCard && isType(anyCard.usedIn, 'ListedOnMarket'));

  const [injury] = anyPlayer.activeInjuries;
  const [suspension] = suspensions;

  let content: ReactNode = null;
  if (anyCard && lineupUsingCard) {
    const so5LeaderboardGroup =
      lineupUsingCard.so5Leaderboard?.so5LeaderboardGroup;

    const so5League = lineupUsingCard.so5Leaderboard?.so5League || null;
    const so5LeagueName =
      lineupUsingCard.so5Leaderboard?.so5TournamentType.so5LeaderboardType ===
      So5LeaderboardType.SPECIAL_WEEKLY
        ? 'Special Event'
        : so5League?.shortDisplayName;
    const so5LeaderboardGroupName = so5League
      ? so5LeagueName
      : so5LeaderboardGroup?.displayName;

    content = (
      <BenchExtraUsedIn color={so5LeaderboardGroup?.color} iconOnly={iconOnly}>
        <FormattedMessage
          id="BenchExtra.usedInName"
          defaultMessage="Used in {name}"
          values={{
            name: isType(anyCard, 'Card')
              ? so5LeaderboardGroupName
              : lineupUsingCard.so5Leaderboard?.shortDisplayName,
          }}
        />
      </BenchExtraUsedIn>
    );
  }
  if (anyCard && (tasksUsingCard || []).length > 0) {
    content = (
      <BenchExtraUsedIn iconOnly={iconOnly}>
        <FormattedMessage
          id="BenchExtra.usedInMission"
          defaultMessage="Used in mission"
        />
      </BenchExtraUsedIn>
    );
  } else if (price) {
    return (
      <BasicBenchExtraWrapper>
        <Horizontal gap={0.5}>
          <LabelS bold>{main}</LabelS>
          {!!exponent && <LabelXS color="var(--c-nd-600)">{exponent}</LabelXS>}
        </Horizontal>
      </BasicBenchExtraWrapper>
    );
  } else if (
    isType(anyCard, 'Card') &&
    [
      CardCoverageStatus.NOT_COVERED,
      CardCoverageStatus.NOT_ELIGIBLE,
      CardCoverageStatus.SPECIAL,
    ].includesValue(anyCard.coverageStatus)
  ) {
    content = (
      <Horizontal gap={0.5} center>
        <Ineligible size={1.5} />
        {!iconOnly && (
          <LabelXS>
            <FormattedMessage
              {...messages[
                anyCard.coverageStatus as
                  | typeof CardCoverageStatus.NOT_COVERED
                  | typeof CardCoverageStatus.NOT_ELIGIBLE
                  | typeof CardCoverageStatus.SPECIAL
              ]}
            />
          </LabelXS>
        )}
      </Horizontal>
    );
  } else if (injury && showInjury) {
    content = <BenchExtraInjury anyPlayer={anyPlayer} iconOnly={iconOnly} />;
  } else if (suspension && showSuspension) {
    content = <BenchExtraSuspension iconOnly={iconOnly} />;
  } else if (listed) {
    content = (
      <Horizontal gap={0.5} center>
        <Market active size={1.5} />
        {!iconOnly && (
          <LabelXS>
            <Ellipsis>
              <FormattedMessage {...fantasy.listedCard} />
            </Ellipsis>
          </LabelXS>
        )}
      </Horizontal>
    );
  } else if (hasMultipleGames && !iconOnly) {
    content = (
      <LabelXS>
        <FormattedMessage
          id="BenchExtra.bestScoreChosen"
          defaultMessage="Best score chosen"
        />
      </LabelXS>
    );
  }
  if (content) {
    return (
      <ConditionalWrapper wrap={!iconOnly} Wrapper={BasicBenchExtraWrapper}>
        <BenchExtraTooltip
          so5LineupId={so5LineupId}
          anyCard={anyCard}
          anyPlayer={anyPlayer}
          suspensions={suspensions}
          showInjury={showInjury}
          showSuspension={showSuspension}
        >
          {content}
        </BenchExtraTooltip>
      </ConditionalWrapper>
    );
  }

  if (iconOnly) {
    return <EmptyIcon />;
  }

  return null;
};

const so5LineupFragment = gql`
  fragment BenchExtra_so5Lineup on So5Lineup {
    id
    so5Leaderboard {
      slug
      shortDisplayName: displayName(short: true, withSeasonality: false)
      so5TournamentType {
        id
        so5LeaderboardType
      }
      so5League {
        slug
        shortDisplayName
      }
      so5LeaderboardGroup {
        slug
        color(variant: PRIMARY)
        displayName
      }
    }
    ...BenchExtraTooltip_so5Lineup
  }
  ${BenchExtraTooltip.fragments.so5Lineup}
` as TypedDocumentNode<BenchExtra_so5Lineup>;

BenchExtra.fragments = {
  anyCard: gql`
    fragment BenchExtra_anyCardInterface on AnyCardInterface {
      slug
      sport
      usedIn(
        so5LeaderboardSlug: $so5LeaderboardSlug
        taskId: $taskId
        gameId: $gameId
      ) {
        slug
        ... on UsedInSo5Lineups {
          slug
          so5Lineups {
            id
            ...BenchExtra_so5Lineup
          }
        }
        ... on ListedOnMarket {
          slug
        }
        ... on UsedInTasks {
          slug
          tasks {
            id
          }
        }
      }
      ... on Card {
        slug
        coverageStatus
      }
      ...BenchExtraTooltip_anyCardInterface
    }
    ${BenchExtraTooltip.fragments.anyCard}
    ${so5LineupFragment}
  ` as TypedDocumentNode<BenchExtra_anyCardInterface>,
  anyPlayer: gql`
    fragment BenchExtra_anyPlayerInterface on AnyPlayerInterface {
      slug
      activeInjuries {
        id
      }
      ...BenchExtraInjury_anyPlayerInterface
      ...BenchExtraTooltip_anyPlayerInterface
    }
    ${BenchExtraTooltip.fragments.anyPlayer}
    ${BenchExtraInjury.fragments.anyPlayer}
  ` as TypedDocumentNode<BenchExtra_anyPlayerInterface>,
  suspension: gql`
    fragment BenchExtra_suspension on Suspension {
      id
      ...BenchExtraTooltip_suspension
    }
    ${BenchExtraTooltip.fragments.suspension}
  ` as TypedDocumentNode<BenchExtra_suspension>,
};
